import React from 'react';

import {
  GetClientsByIdsItem,
  useGetClientsByIds,
} from '../data/useGetClientsById';
import {
  GetEntitiesByIdsItem,
  useGetEntitiesByIds,
} from '../data/useGetEntitiesByIds';
import {
  GetMemberListItem,
  useGetMembersList,
} from '../data/useGetMembersList';
import { GetJobsByIdsResult } from '../data/useGetJobsByIds';
import { useSearchTasksWithJob } from '../data/useSearchTasksWithJob';
import { SearchTasksWithJobResult } from '../data/useSearchTasksWithJob';
import {
  QuerySearchTasksArgs,
  searchTasksArgsToGqlSearchTasksArgs,
} from '../types/mapper';
import {
  GetJobStatusListResult,
  useGetJobStatusList,
} from '../data/useGetJobStatusList';

export interface TaskWithJob extends Omit<SearchTasksWithJobResult, 'job'> {
  job: GetJobsByIdsResult & {
    client?: GetClientsByIdsItem;
    status: GetJobStatusListResult;
  };
  assignee?: GetMemberListItem;
  entity?: GetEntitiesByIdsItem;
  estimateHoursDisplay?: string;
}

export const useQueryTasksWithJob = ({
  variables,
  skip,
}: {
  variables?: QuerySearchTasksArgs;
  skip?: boolean;
}): {
  loading?: boolean;
  error?: boolean;
  data?: TaskWithJob[];
} => {
  const members = useGetMembersList({ skip });
  const statuses = useGetJobStatusList({ skip });
  const tasks = useSearchTasksWithJob({
    variables:
      skip || !variables
        ? undefined
        : searchTasksArgsToGqlSearchTasksArgs(variables),
    skip,
  });

  const jobs = tasks.data?.map((task) => {
    return task.job;
  });

  const clientIds = [
    ...new Set(
      jobs
        ?.map((x) => x?.clientId)
        .filter(<T>(x: T): x is NonNullable<T> => Boolean(x))
    ),
  ];

  const clients = useGetClientsByIds(
    !skip && clientIds && clientIds.length > 0
      ? {
          variables: { cloudIds: clientIds },
        }
      : { skip: true }
  );

  const entityIds = [
    ...new Set(
      tasks.data
        ?.map(({ entityId }) => entityId)
        .filter(<T>(x: T): x is NonNullable<T> => Boolean(x))
    ),
  ];

  const entities = useGetEntitiesByIds(
    !skip && entityIds && entityIds.length > 0
      ? {
          variables: { cloudIds: entityIds },
        }
      : { skip: true }
  );

  return React.useMemo(() => {
    if (skip) return {};

    if (
      clients.loading ||
      entities.loading ||
      members.loading ||
      tasks.loading ||
      statuses.loading
    )
      return { loading: true };
    if (
      clients.error ||
      entities.error ||
      members.error ||
      tasks.error ||
      statuses.error
    )
      return { error: true };

    const data = tasks.data?.map((task) => {
      const job = task.job;
      const client =
        (job &&
          clients.data?.find((client) => client.cloudId === job.clientId)) ||
        undefined;
      const assignee = members.data?.find(
        (member) => member.cloudId === task.assignedToUserId
      );
      const entity = entities.data?.find(
        (entity) => entity.cloudId === task.entityId
      );
      const status =
        job && statuses.data?.find((x) => x.cloudId === job.jobStatusId);
      if (job && !status) {
        console.error(
          `Query Tasks: unable to find status "${job.jobStatusId}" for job "${job.cloudId}"`
        );
      }
      const estimateHoursDisplay =
        typeof task.estimateMinutes === 'number'
          ? (task.estimateMinutes / 60).toFixed(2)
          : undefined;
      const result: TaskWithJob = {
        ...task,
        job: {
          ...job!, // a task MUST have a job in the database. If we can't find it, the whole system is broken
          client,
          status: status!, // a job MUST have a status. If we can't find it, we have bigger problems
        },
        assignee,
        entity,
        estimateHoursDisplay,
      };
      return result;
    });

    return { data };
  }, [clients, entities, members, tasks, statuses, skip]);
};
